/**
 * RiskProtectionsSection
 * Displays and manages EPI/EPC protections linked to an occupational risk
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Switch,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import riskProtectionService, {
  RiskProtection,
} from '../../../services/riskProtectionService';
import { ActionButton, DataTable, type DataTableColumn } from '../../../components/common';
import { useAcao } from '../../../authorization/useVeredito';
import { AddProtectionDialog } from './AddProtectionDialog';

interface RiskProtectionsSectionProps {
  riskId: number | string;
}

export const RiskProtectionsSection: React.FC<RiskProtectionsSectionProps> = ({ riskId }) => {
  const alternarEficacia = useAcao('occupational_risk.update', 'Marcar como eficaz');
  const desvincular = useAcao('occupational_risk.update', 'Desvincular');
  const [protections, setProtections] = useState<RiskProtection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'EPI' | 'EPC'>('EPI');

  const loadProtections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await riskProtectionService.getProtections(riskId);
      setProtections(result.protections);
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } } };
      setError(apiError?.response?.data?.error || 'Erro ao carregar proteções');
    } finally {
      setLoading(false);
    }
  }, [riskId]);

  useEffect(() => {
    loadProtections();
  }, [loadProtections]);

  const handleOpenDialog = (type: 'EPI' | 'EPC') => {
    setDialogType(type);
    setDialogOpen(true);
  };

  const handleCreateProtection = async (data: {
    epiId?: number | null;
    epcId?: number | null;
    isEffective: boolean;
    notes?: string;
  }) => {
    await riskProtectionService.createProtection(riskId, data);
    await loadProtections();
  };

  const handleToggleEffective = async (protection: RiskProtection) => {
    try {
      await riskProtectionService.updateProtection(riskId, protection.id, {
        isEffective: !protection.isEffective,
      });
      await loadProtections();
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } } };
      setError(apiError?.response?.data?.error || 'Erro ao atualizar proteção');
    }
  };

  const handleDelete = async (protectionId: number) => {
    try {
      await riskProtectionService.deleteProtection(riskId, protectionId);
      await loadProtections();
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } } };
      setError(apiError?.response?.data?.error || 'Erro ao remover proteção');
    }
  };

  const getProtectionType = (p: RiskProtection): string => {
    if (p.epiId && p.epcId) return 'EPI + EPC';
    if (p.epiId) return 'EPI';
    return 'EPC';
  };

  const getProtectionName = (p: RiskProtection): string => {
    if (p.epi) return p.epi.nome;
    if (p.epc) return p.epc.nome;
    return '-';
  };

  const getProtectionDetail = (p: RiskProtection): string => {
    if (p.epi?.metodo) return `CA: ${p.epi.metodo}`;
    if (p.epc?.revisao) return `Rev: ${p.epc.revisao}`;
    return '-';
  };

  const columns: DataTableColumn<RiskProtection>[] = [
    {
      id: 'type',
      label: 'Tipo',
      render: (p) => (
        <Chip
          label={getProtectionType(p)}
          size="small"
          color={p.epiId ? 'primary' : 'secondary'}
          variant="outlined"
        />
      ),
    },
    {
      id: 'name',
      label: 'Nome',
      primary: true,
      render: (p) => getProtectionName(p),
    },
    {
      id: 'detail',
      label: 'CA / Revisão',
      render: (p) => getProtectionDetail(p),
    },
    {
      id: 'effective',
      label: 'Eficaz',
      align: 'center',
      render: (p) => (
        <Tooltip title={alternarEficacia.tooltip}>
          <span>
            <Switch
              size="small"
              checked={p.isEffective}
              disabled={alternarEficacia.disabled}
              onChange={() => handleToggleEffective(p)}
              inputProps={{ 'aria-label': alternarEficacia.tooltip }}
            />
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'notes',
      label: 'Observações',
      render: (p) => p.notes || '-',
    },
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldIcon color="primary" />
          <Typography variant="h6">
            Proteções (EPC/EPI)
          </Typography>
          <Chip label={protections.length} size="small" color="primary" variant="outlined" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ActionButton
            acao="occupational_risk.update"
            rotulo="Vincular EPI"
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('EPI')}
          >
            Vincular EPI
          </ActionButton>
          <ActionButton
            acao="occupational_risk.update"
            rotulo="Vincular EPC"
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('EPC')}
          >
            Vincular EPC
          </ActionButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : protections.length === 0 ? (
        <Alert severity="info">
          Nenhuma proteção vinculada a este risco.
        </Alert>
      ) : (
        <DataTable
          columns={columns}
          rows={protections}
          getRowId={(p) => p.id}
          emptyState={{
            title: 'Nenhuma proteção vinculada a este risco',
            description:
              'Use os botões "Vincular EPI" e "Vincular EPC" acima para vincular as proteções deste risco.',
          }}
          actions={[
            {
              icon: <DeleteIcon fontSize="small" />,
              color: 'error',
              onClick: (p) => handleDelete(p.id),
              ...desvincular,
            },
          ]}
        />
      )}

      <AddProtectionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleCreateProtection}
        protectionType={dialogType}
      />
    </Box>
  );
};

export default RiskProtectionsSection;
